import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { ModalService } from '@app/core/services/modal/modal.service';
import { Modal } from '@app/core/interfaces/modal.interface';
import {
  IConfirmModalButtonStyle,
  ModalButtonStyleService,
} from '@app/core/services/modal/modal-button-style.service';
import { Button, ButtonSeverity } from '@openng/optimus-ui/button';
import { Dialog } from '@openng/optimus-ui/dialog';

export interface ModalBaseConfig {
  title: string;
  canConfirm?: boolean;
  canCancel?: boolean;
  onConfirm: (res?: any) => void;
  onConfirmClickOverride?: (res?: any) => void;
  onCancel?: (res?: any) => void;
  confirmBtnText?: string;
  confirmBtnClass?: string;
  cancelBtnText?: string;
}

@Component({
  selector: 'ag-modal-base',
  templateUrl: './modal-base.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [Button, Dialog],
})
export class ModalBaseComponent implements Modal {
  private _modalService = inject(ModalService);
  private _modalButtonStyleService = inject(ModalButtonStyleService);

  public title: string;
  public content: string;
  public confirmBtnText: string;
  public confirmBtnClass: string;
  public cancelBtnText: string;
  public onConfirm: (res?: any) => void;
  public onCancel: (res?: any) => void;
  public isLoading = false;
  public isVisible = false;
  public canConfirm = true;
  public canCancel = true;

  public onAction: Promise<boolean>;
  public onActionResolve: (action: boolean) => void;

  private _closingProgrammatically = false;
  private _resolveHidePromise: (() => void) | null = null;
  private _rejectHidePromise: ((reason?: unknown) => void) | null = null;
  private readonly _defaultHide = (): void => {
    void this.hide();
  };

  constructor() {
    this.onAction = new Promise((resolve) => (this.onActionResolve = resolve));
  }

  configure(config: ModalBaseConfig) {
    this.title = config.title;
    this.confirmBtnClass = config.confirmBtnClass || 'is-primary';
    this.confirmBtnText = config.confirmBtnText || 'Ok';
    this.cancelBtnText = config.cancelBtnText || 'Cancel';
    this.canConfirm =
      config.canConfirm === undefined ? true : config.canConfirm;
    this.canCancel = config.canCancel === undefined ? true : config.canCancel;
    this.onConfirm = config.onConfirm;
    this.onCancel = config.onCancel || this._defaultHide;
  }

  get confirmButtonStyle(): IConfirmModalButtonStyle & {
    severity?: ButtonSeverity;
    link?: boolean;
  } {
    return this._modalButtonStyleService.toConfirmButtonStyle(
      this.confirmBtnClass,
    );
  }

  setTitle = (title: string) => (this.title = title);
  setLoading = (isLoading: boolean) => (this.isLoading = isLoading);
  setConfirmBtnText = (text: string) => (this.confirmBtnText = text);
  setCancelBtnText = (text: string) => (this.cancelBtnText = text);
  setOnConfirm = (onConfirm: (res?: any) => void) =>
    (this.onConfirm = onConfirm);
  setOnCancel = (onCancel: (res?: any) => void) => (this.onCancel = onCancel);
  setCanConfirm = (canConfirm: boolean) => (this.canConfirm = canConfirm);
  setCanCancel = (canCancel: boolean) => (this.canCancel = canCancel);

  show = () => {
    this._closingProgrammatically = false;
    this.isVisible = true;
  };

  hide(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.isVisible) {
        resolve();
        return;
      }

      this._closingProgrammatically = true;
      this._resolveHidePromise = resolve;
      this._rejectHidePromise = reject;
      this.isVisible = false;
    });
  }

  onDialogHide() {
    if (!this._closingProgrammatically && !this.isLoading) {
      this.onActionResolve(false);
      if (this.onCancel !== this._defaultHide) {
        this.onCancel();
      }
    }

    if (this._closingProgrammatically) {
      this._resolveHidePromise?.();
    }

    try {
      this._modalService.clearHost();
    } catch (err) {
      this._rejectHidePromise?.(err);
    } finally {
      this._closingProgrammatically = false;
      this._resolveHidePromise = null;
      this._rejectHidePromise = null;
    }
  }

  onConfirmClick() {
    this.onActionResolve(true);
    this.onConfirm();
  }

  onCancelClick() {
    if (this.isLoading) {
      return;
    }

    this.onActionResolve(false);

    if (this.onCancel === this._defaultHide) {
      void this.hide();
      return;
    }

    this.onCancel();
  }
}
